import React, { useEffect, useState } from 'react';
import {
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiAlertTriangle,
  FiX
} from 'react-icons/fi';

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number; // en ms, 0 pour permanent
  actionLabel?: string;
  onAction?: () => void;
  onClose?: () => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  actionLabel,
  onAction,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Animation d'entrée
    setTimeout(() => setIsVisible(true), 10);

    // Timer pour fermer automatiquement
    if (duration > 0) {
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - (100 / (duration / 100));
          if (newProgress <= 0) {
            handleClose();
            return 0;
          }
          return newProgress;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300); // Attendre la fin de l'animation
  };

  const handleAction = () => {
    if (onAction) onAction();
    handleClose();
  };

  const config = {
    success: {
      icon: FiCheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      iconColor: 'text-green-600',
      titleColor: 'text-green-900',
      textColor: 'text-green-700',
      progressColor: 'bg-green-500'
    },
    error: {
      icon: FiAlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-600',
      titleColor: 'text-red-900',
      textColor: 'text-red-700',
      progressColor: 'bg-red-500'
    },
    warning: {
      icon: FiAlertTriangle,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      iconColor: 'text-orange-600',
      titleColor: 'text-orange-900',
      textColor: 'text-orange-700',
      progressColor: 'bg-orange-500'
    },
    info: {
      icon: FiInfo,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600',
      titleColor: 'text-blue-900',
      textColor: 'text-blue-700',
      progressColor: 'bg-blue-500'
    }
  };

  const styles = config[type];
  const Icon = styles.icon;

  return (
    <div
      className={`${styles.bgColor} ${styles.borderColor} border-2 rounded-xl p-4 shadow-lg mb-3 transition-all duration-300 transform ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      } max-w-md w-full`}
      style={{ minWidth: '320px' }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={styles.iconColor} size={24} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm ${styles.titleColor}`}>{title}</h4>
          {message && (
            <p className={`text-sm mt-1 ${styles.textColor}`}>{message}</p>
          )}

          {/* Action button */}
          {actionLabel && onAction && (
            <button
              onClick={handleAction}
              className={`mt-3 px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                type === 'success'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : type === 'error'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : type === 'warning'
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {actionLabel}
            </button>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className={`p-1 rounded-lg transition ${
            type === 'success'
              ? 'hover:bg-green-100'
              : type === 'error'
              ? 'hover:bg-red-100'
              : type === 'warning'
              ? 'hover:bg-orange-100'
              : 'hover:bg-blue-100'
          }`}
        >
          <FiX size={18} className="text-gray-500" />
        </button>
      </div>

      {/* Progress bar */}
      {duration > 0 && (
        <div className="mt-3 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${styles.progressColor} transition-all duration-100 ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
